import React, { useState, useEffect } from 'react';
import { Search, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categoriesAPI, citiesAPI } from '../services/api';

const IMAGE_SRC = '/images/hero-khadam.png?v=2';

const Hero = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    categoriesAPI.getAll().then((res) => setCategories(res.data)).catch(() => {});
    citiesAPI.getAll().then((res) => setCities(res.data)).catch(() => {});
  }, []);

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const match = categories.find((cat) =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (match) {
        const query = cityQuery ? `?city=${encodeURIComponent(cityQuery)}` : '';
        navigate(`/category/${match.id}${query}`);
      }
    }
    setShowSuggestions(false);
  };

  return (
    <section className="relative w-full bg-white" data-testid="hero-section">
      {/* Desktop / Tablet : full custom banner with overlayed functional search */}
      <div className="hidden md:block relative w-full pt-16">
        <img
          src={IMAGE_SRC}
          alt="Khadam Express — Trouvez le bon prestataire, en toute confiance"
          className="w-full h-auto block select-none"
          draggable={false}
        />
        {/* Overlay functional search bar aligned on banner's visual search (2 fields + button) */}
        <form
          onSubmit={handleSearch}
          className="absolute z-20"
          style={{ top: '70%', left: '7%', width: '48%' }}
        >
          <div className="flex items-center bg-white rounded-full shadow-xl pl-4 pr-1.5 h-[clamp(48px,4.5vw,64px)] border border-gray-100">
            <Search size={18} className="text-gray-400 flex-shrink-0 mr-2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(e.target.value.length > 0);
              }}
              onFocus={() => searchQuery && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder={t('hero.searchPlaceholder')}
              data-testid="hero-search-input"
              className="flex-1 min-w-0 bg-transparent outline-none text-gray-800 text-sm md:text-base placeholder:text-gray-400"
            />
            <div className="h-6 w-px bg-gray-200 mx-3 flex-shrink-0" />
            <MapPin size={18} className="text-gray-400 flex-shrink-0 mr-2" />
            <input
              type="text"
              value={cityQuery}
              onChange={(e) => setCityQuery(e.target.value)}
              list="hero-cities-list"
              placeholder={t('hero.cityPlaceholder')}
              data-testid="hero-city-input"
              className="w-[30%] min-w-0 bg-transparent outline-none text-gray-800 text-sm md:text-base placeholder:text-gray-400"
            />
            <datalist id="hero-cities-list">
              {cities.map((c) => (
                <option key={c.id} value={c.name} />
              ))}
            </datalist>
            <button
              type="submit"
              data-testid="hero-search-submit"
              className="h-[clamp(40px,3.8vw,52px)] px-[clamp(18px,2.2vw,30px)] bg-orange-500 hover:bg-orange-600 rounded-full text-white text-sm md:text-base font-semibold transition-colors flex-shrink-0"
            >
              {t('hero.searchButton')}
            </button>
          </div>

          {showSuggestions && filteredCategories.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl overflow-hidden z-50 max-h-[300px] overflow-y-auto border border-gray-100">
              {filteredCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onMouseDown={() => {
                    navigate(`/category/${cat.id}`);
                    setShowSuggestions(false);
                    setSearchQuery('');
                  }}
                  className="w-full px-5 py-3 text-left text-gray-700 hover:bg-blue-50 text-sm transition-colors border-b border-gray-50 last:border-0 flex items-center gap-3"
                >
                  <span className="text-2xl">{cat.icon}</span>
                  <span className="font-medium">{cat.name}</span>
                </button>
              ))}
            </div>
          )}
        </form>
      </div>

      {/* Mobile */}
      <div className="md:hidden pt-16">
        <img src={IMAGE_SRC} alt="Khadam Express" className="w-full h-auto block" />
        <div className="px-4 py-5 bg-white -mt-2">
          <form onSubmit={handleSearch} className="space-y-3">
            <div className="flex items-center bg-white rounded-full shadow-md border border-gray-100 h-[52px] px-4">
              <Search size={18} className="text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('hero.searchPlaceholder')}
                className="flex-1 bg-transparent outline-none text-gray-800 text-sm placeholder:text-gray-400 ml-2"
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center bg-white rounded-full shadow-md border border-gray-100 h-[52px] px-4">
                <MapPin size={18} className="text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={cityQuery}
                  onChange={(e) => setCityQuery(e.target.value)}
                  list="hero-cities-list-mobile"
                  placeholder={t('hero.cityPlaceholder')}
                  className="flex-1 bg-transparent outline-none text-gray-800 text-sm placeholder:text-gray-400 ml-2"
                />
                <datalist id="hero-cities-list-mobile">
                  {cities.map((c) => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
              </div>
              <button
                type="submit"
                className="px-5 h-[52px] bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full flex-shrink-0"
              >
                {t('hero.searchButton')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Hero;
